// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Slide, useTheme } from '@mui/material';
import { ReactMuiTableV8 } from '@nuva-org/solytics-frontend';
import { useContextMenu } from 'react-contexify';
import { isEmpty, debounce } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DownloadIcon from '@mui/icons-material/Download';

import Breadcrumb from '../../components/Common/Breadcrumb';
import { ContextMenus } from '../../components/Common/ContextMenus';
import useModelInventory from '../../hooks/ModelInventory/useModelInventory';
import useCommon from '../../hooks/useCommon';
import SearchTextField from '../../components/Common/SearchTextField';
import swalToast from '../../components/Common/swalToast';
import {
  getEntityTypeKey,
  getEntityTypes,
  getLabelPathFromLabelConfig,
} from '../../utils/Utils';
import { DEFAULT_ROUTE_CONFIG } from '../../const/defaultRoutesConfig';
import {
  ENTITY_TYPE,
  PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../../const/CommonConst';
import NoDataPresentComponent from '../../components/Common/NoDataPresentComponent';
import { useTableMaxHeight } from '../../hooks/Configuration/useTableMaxHeight';

const INITIAL_PAGE_SIZE = PAGE_SIZE || 10;

const getEntityRoutes = () => ({
  SUBPROCESS: getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.ModelAssociation?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.ModelAssociation?.path
  ),
  USECASES: getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.useCase?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.useCase?.path
  ),
  ASSESSMENTS: getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.modelRequest?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.modelRequest?.path
  ),
  MODELS: getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.modelInventory?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.modelInventory?.path
  ),
});

const ENTITY_ROUTES = getEntityRoutes();

const getEntityLabel = ({ isSubProcess, isUseCase, isAssessment }) => {
  if (isSubProcess) {
    return getLabelPathFromLabelConfig(
      'TEMPLATE_TYPES.ModelAssociation',
      'featureLabel',
      'Sub-Process'
    );
  }

  if (isUseCase) {
    return getLabelPathFromLabelConfig(
      'TEMPLATE_TYPES.UseCase',
      'featureLabel',
      'UseCase'
    );
  }

  if (isAssessment) {
    return getLabelPathFromLabelConfig(
      'TEMPLATE_TYPES.ModelRequest',
      'featureLabel',
      'Assessment'
    );
  }

  return getLabelPathFromLabelConfig(
    'TEMPLATE_TYPES.ModelInventory',
    'featureLabel',
    'Model'
  );
};

const ArchivedEntity = () => {
  const theme = useTheme();

  const { getAllModelEntity, archiveEntity, downloadEntitiesReport } =
    useModelInventory();
  const { common, dispatch, setModelArchivedSearch } = useCommon();
  const location = useLocation();
  const paths = location.pathname.split('/').slice(1);

  const isSubProcess = paths.includes(ENTITY_ROUTES.SUBPROCESS);
  const isUseCase = paths.includes(ENTITY_ROUTES.USECASES);
  const isAssessment = paths.includes(ENTITY_ROUTES.ASSESSMENTS);

  const [selectedRow, setSelectedRow] = useState(null);
  const [archivedEntityList, setArchivedEntityList] = useState([]);
  const [tableRowCount, setTableRowCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [skipReset, setSkipReset] = useState(true);
  const [currentPageSize, setCurrentPageSize] = useState(INITIAL_PAGE_SIZE);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [loading, setLoading] = useState(true);

  const { maxTableHeight } = useTableMaxHeight();

  const history = useNavigate();

  const { show: showRowLevelMenus, hideAll } = useContextMenu();

  const columns = useMemo(() => {
    const entityLabel = getEntityLabel({
      isSubProcess,
      isUseCase,
      isAssessment,
    });
    return [
      {
        Header: 'UNIQUE ID',
        accessor: 'unique_id',
      },
      {
        Header: `${entityLabel} NAME`,
        accessor: 'entity_name',
      },
      {
        Header: 'WORKFLOW NAME',
        accessor: 'workflow',
      },
    ];
  }, [isSubProcess, isUseCase, isAssessment]);

  //dynamic payload based on the entity
  const payloadConfig = useMemo(() => {
    if (paths.includes(ENTITY_ROUTES.USECASES)) {
      return {
        entityType: 'ModelInventory',
        requestType: 'UseCase',
      };
    }

    if (paths.includes(ENTITY_ROUTES.ASSESSMENTS)) {
      return {
        entityType: 'ModelInventory',
        requestType: 'ModelRequest',
      };
    }

    if (paths.includes(ENTITY_ROUTES.SUBPROCESS)) {
      return {
        entityType: 'ModelAssociation',
      };
    }

    return {
      entityType: 'ModelInventory',
    };
  }, [paths]);

  /**
   * Handles the change event for Model Inventory Search input element.
   * If the input value starts with a space character, prevents the default behavior.
   * Otherwise, dispatches an action to update the Model Inventory search.
   * @param {Event} e - The change event object.
   * @returns {void}
   */
  const handleInputChange = useCallback(
    (e) => {
      if (e?.target?.value.startsWith(' ')) {
        e?.preventDefault();
        return;
      }
      dispatch(setModelArchivedSearch(e?.target?.value));
    },
    [setModelArchivedSearch, dispatch]
  );

  /**
   * @function handleModelView
   * @description  to open respective Model
   * @param row- details of model
   */
  const handleModelView = (row) => {
    const entityTypeKey = getEntityTypeKey(
      row?.original?.entity_type,
      row?.original?.request_type
    );
    const uniqueId = row?.original?.unique_id;

    const dynamicEntityPath = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
      'path',
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
    );

    const dynamicEntityLabel = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
      'label',
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
    );

    let archivePath;

    if (isSubProcess) {
      archivePath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.archivedArtifacts?.keyPath || ``,
        'path',
        DEFAULT_ROUTE_CONFIG?.archivedArtifacts?.path
      );
    } else if (isUseCase) {
      archivePath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.archivedUseCase?.keyPath || ``,
        'path',
        DEFAULT_ROUTE_CONFIG?.archivedUseCase?.path
      );
    } else if (isAssessment) {
      archivePath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.archivedModelRequest?.keyPath || ``,
        'path',
        DEFAULT_ROUTE_CONFIG?.archivedModelRequest?.path
      );
    } else {
      archivePath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.archivedModels?.keyPath,
        'path',
        DEFAULT_ROUTE_CONFIG?.archivedModels?.path
      );
    }

    history(
      {
        pathname: `/${dynamicEntityPath}/${uniqueId}`,
      },
      {
        state: [
          {
            label: dynamicEntityLabel,
            path: `/${dynamicEntityPath}`,
          },
          {
            label: 'Archive',
            path: `/${archivePath}`,
          },
          {
            label: uniqueId,
            path: `/${dynamicEntityPath}/${uniqueId}`,
            type: ENTITY_TYPE[
              getEntityTypes(
                row?.original?.entity_type,
                row?.original?.request_type
              )
            ],
          },
        ],
      }
    );
  };

  const handelChangeSkipReset = useCallback(
    (pageIndex) => {
      if (pageIndex === 1) {
        setSkipReset(false);
      } else {
        setSkipReset(true);
      }
    },
    [setSkipReset]
  );

  /**
   * @function getModelDetails
   * @description get Model Details according to condition
   * @param {text} -user search test
   * @param {globalSearch}- boolean global search is enabled or not
   * @param {_filterName} - if user selected any filter then its name
   * @param {pageSize} - size of page
   * @param {pageIndex} - page index of data
   * @param {filterType} - type of filter like previewSearch and savedSearch
   * @param {removeFilter} - it is used to decide remove filterType search params
   */

  const getModelDetails = useCallback(
    ({
      text = '',
      pageSize = currentPageSize,
      pageIndex = currentPageIndex,
      globalSearch = false,
      condition = null,
    }) => {
      handelChangeSkipReset(pageIndex);

      const checkGlobalSearch = () => {
        if (!isEmpty(common?.archivedModelSearch) || globalSearch) {
          return { globalSearch: true };
        }
        return null;
      };
      setLoading(true);
      getAllModelEntity({
        ...payloadConfig,
        pageIndex,
        pageSize,
        ...(!isEmpty(text) ? { text } : null),
        ...checkGlobalSearch(),
        ...(!isEmpty(condition)
          ? { condition: JSON.stringify(condition) }
          : null),
        ...(!isEmpty(common?.archivedModelSearch)
          ? { text: common?.archivedModelSearch }
          : null),
        isArchived: true,
      })
        .then((res) => {
          setArchivedEntityList(res?.data);
          const countRows = res.headers.get('count');
          setArchivedEntityList(res?.data);
          setTableRowCount(countRows);

          setPageCount(Math.ceil(countRows / pageSize));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      setPageCount,
      setTableRowCount,
      setArchivedEntityList,
      isEmpty,
      getAllModelEntity,
      setSkipReset,
      common?.archivedModelSearch,
    ]
  );

  const fetchData = () => {
    if (common?.archivedModelSearch?.trim().length > 0) {
      getModelDetails({
        text: common?.archivedModelSearch,
        globalSearch: true,
        pageIndex: 1,
        pageSize: INITIAL_PAGE_SIZE,
      });
    } else {
      getModelDetails({});
    }
  };
  const debouncedFetchData = debounce(fetchData, 800);

  useEffect(() => {
    debouncedFetchData();
    return () => {
      debouncedFetchData.cancel();
    };
  }, [common?.archivedModelSearch]);

  const handleUnArchiveModel = useCallback(() => {
    const body = {
      entity_id: selectedRow?.original?.entity_id,
      entity_type: payloadConfig?.entityType,
      is_archived: false,
    };
    archiveEntity(body).then((res) => {
      if (res?.data) {
        setSelectedRow(null);
        getModelDetails({
          pageIndex: 1,
          pageSize: currentPageSize,
        });
        swalToast({ icon: 'success', title: res?.data?.msg });
      }
    });
  }, [selectedRow, setSelectedRow, getModelDetails, swalToast, archiveEntity]);

  /**
   * @function menus
   * @description  array of different types of operation which will perform on specific model
   */
  const menus = useMemo(() => {
    const entityLabel = getEntityLabel({
      isSubProcess,
      isUseCase,
      isAssessment,
    }).toLowerCase();
    return [
      {
        label: `View ${entityLabel} details`,
        id: 'view_model_details',
        icon: VisibilityIcon,
        onClick: ({ props: row }) => handleModelView(row),
      },
      {
        label: `Unarchive ${entityLabel}`,
        id: 'unarchive_',
        icon: UnarchiveIcon,
        onClick: ({ props: row }) => handleUnArchiveModel(row?.original),
      },
    ];
  }, [
    isSubProcess,
    isUseCase,
    isAssessment,
    handleModelView,
    handleUnArchiveModel,
  ]);

  const fetchDatasetRows = useCallback(
    (pageIndex, pageSize) => {
      setSkipReset(true);
      setLoading(true);
      getAllModelEntity({
        ...payloadConfig,
        pageIndex: Number(pageIndex + 1),
        pageSize,
        ...(!isEmpty(common?.archivedModelSearch)
          ? { text: common?.archivedModelSearch }
          : null),
        ...(!isEmpty(common?.archivedModelSearch)
          ? { globalSearch: true }
          : null),
        isArchived: true,
      })
        .then((res) => {
          if (res) {
            const countRows = res.headers.get('count');
            setCurrentPageSize(pageSize);
            setCurrentPageIndex(pageIndex + 1);
            setArchivedEntityList(res?.data);
            setTableRowCount(countRows);
            setPageCount(Math.ceil(countRows / pageSize));
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      setArchivedEntityList,
      setTableRowCount,
      setPageCount,
      common?.archivedModelSearch,
    ]
  );

  const handleGetRowProps = useCallback(
    (row) => {
      return {
        onContextMenu: (e) => {
          setSelectedRow(row);
          showRowLevelMenus({
            id: 'model-inventory-table-menus',
            event: e,
            props: row,
          });
        },
        style: {
          backgroundColor:
            row?.id === selectedRow?.id ? theme?.palette?.other?.menuHover : '',
          cursor: 'pointer',
        },
      };
    },
    [selectedRow, showRowLevelMenus, setSelectedRow]
  );

  const handelGetHeaderProps = useCallback(() => {
    return {
      style: {
        display: 'flex',
        alignItems: 'center',
      },
    };
  }, []);

  const handleGetColumnProps = useCallback(() => {
    return {
      style: {
        textAlign: 'left',
        textOverflow: 'ellipsis',
        overflow: ' hidden',
        wordBreak: 'initial',
        whiteSpace: 'nowrap',
        paddingLeft: 10,
      },
    };
  }, []);

  const handelGetCellProps = useCallback(
    (cell) => {
      return {
        onContextMenu: (e) => {
          setSelectedRow(cell?.row);
          showRowLevelMenus({
            id: 'model-inventory-table-menus',
            event: e,
            props: cell?.row,
          });
        },
        style: {
          backgroundColor:
            cell?.row?.id === selectedRow?.id
              ? theme?.palette?.other?.menuHover
              : '',
          position: 'relative',
        },
        onDoubleClick: () => {
          handleModelView(cell?.row);
        },
      };
    },
    [selectedRow, showRowLevelMenus, setSelectedRow]
  );

  const renderNoData = () => {
    return archivedEntityList?.length <= 0 && !loading ? (
      <Box
        display="flex"
        style={{ height: 'calc(100vh  - 260px)' }}
        flexGrow={1}
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
      >
        <NoDataPresentComponent message="No data result found." />
      </Box>
    ) : null;
  };

  const handleMenuExit = useCallback(() => {
    setSelectedRow(null);
  }, [setSelectedRow]);

  const downloadReport = useCallback(() => {
    downloadEntitiesReport({
      ...payloadConfig,
      ...(!isEmpty(common?.archivedModelSearch)
        ? {
            text: common?.archivedModelSearch,
          }
        : null),
      isArchived: true,
    }).then((response) => {
      try {
        const blob = new Blob([response?.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        // Create a temporary URL for the Blob
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ModelInventoryReport.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        swalToast({
          icon: 'success',
          title: `File downloaded successfully.`,
        });
      } catch (e) {
        swalToast({
          icon: 'error',
          title: `Error occurred at time of downloading file. ${e}`,
        });
      }
    });
  }, [common?.archivedModelSearch, downloadEntitiesReport]);

  const renderDownloadReportButton = () => {
    if (archivedEntityList?.length <= 0) return null;
    return (
      <Box>
        <IconButton
          aria-label="Download table data"
          title="Download table data"
          color="primary"
          className="outlined"
          tabIndex={0}
          onClick={downloadReport}
        >
          <DownloadIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <Grid component="main" container sx={{ px: 3, mt: 7, pt: 2 }} rowGap={2}>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Box>
          <Breadcrumb />
        </Box>
      </Grid>
      <Grid item xs={12} display="flex" justifyContent="space-between">
        <SearchTextField
          value={common?.archivedModelSearch}
          onChange={handleInputChange}
          placeholder="Search"
        />
        {renderDownloadReportButton()}
      </Grid>
      <Grid item xs={12} style={{ height: 'calc(100vh - 180px)' }} px={2}>
        <Box display="flex" flexGrow={1} flexDirection="column" overflow="auto">
          {archivedEntityList?.length > 0 ? (
            <Slide direction="left" timeout={1000} mountOnEnter in={true}>
              <Box onScrollCapture={hideAll}>
                <ReactMuiTableV8
                  maxTableHeight={maxTableHeight}
                  fetchData={fetchDatasetRows}
                  data={
                    archivedEntityList?.length > 0 ? archivedEntityList : []
                  }
                  columns={columns}
                  initialHiddenColumns={[]}
                  dataLength={tableRowCount}
                  enableControlledPagination={true}
                  getHeaderProps={handelGetHeaderProps}
                  getColumnProps={handleGetColumnProps}
                  getRowProps={handleGetRowProps}
                  stickyHeader={true}
                  stickyHeaderConfig={{
                    containerStyle: {
                      height: '100%',
                      overflow: 'auto',
                      width: '100%',
                    },
                    tableHeadStyle: { zIndex: 1 },
                  }}
                  enableRowSelection={false}
                  pageCount={pageCount}
                  enablePagination
                  initialPageSize={INITIAL_PAGE_SIZE}
                  rowsPerPageOptions={TABLE_PAGE_SIZE_OPTIONS}
                  isAllOptionDisabled={true}
                  showOptions={false}
                  showToolbar={false}
                  skipPageReset={skipReset}
                  getCellProps={handelGetCellProps}
                />
                <ContextMenus
                  menuId="model-inventory-table-menus"
                  menus={menus}
                  onMenuExit={handleMenuExit}
                />
              </Box>
            </Slide>
          ) : null}
          {renderNoData()}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ArchivedEntity;
